<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_PLAYERS, INITIAL_TEAMS } from '@/services/mockData';
import AppBadge from '@/components/ui/AppBadge.vue';
import { useToastNotification } from '@/composables/useToastNotification';
import { NButton, NSelect, NSlider, NTag } from 'naive-ui';
import {
  Video,
  Play,
  Pause,
  RotateCcw,
  Sparkles,
  Gauge,
  Crosshair,
  Layers,
  ChevronRight,
  Maximize2,
  Bookmark,
  Share2,
  Flame,
  Activity,
  SkipBack,
  SkipForward
} from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant } = useTenant();
const { notifySuccess, notifyInfo } = useToastNotification();

const isPlaying = ref(false);
const playbackSpeed = ref<number>(1);
const scrubProgress = ref(42);
const activeAngle = ref<'HIGH_HOME' | 'OPEN_SIDE' | 'TIGHT_ZONE'>('HIGH_HOME');
const selectedPlayerId = ref(INITIAL_PLAYERS[0].id);

const selectedPlayer = computed(() =>
  INITIAL_PLAYERS.find(p => p.id === selectedPlayerId.value) || INITIAL_PLAYERS[0]
);

const playerOptions = computed(() =>
  INITIAL_PLAYERS.filter(p => p.tenantId === activeTenant.value.id).map(p => ({
    label: `#${p.jerseyNumber} ${p.firstName} ${p.lastName} (${p.position})`,
    value: p.id
  }))
);

const clips = ref([
  {
    id: 'clip_1',
    title: '4-Seam Fastball vs Low & Away (Strike 3)',
    velo: '94.2 MPH',
    spin: '2,340 RPM',
    vBreak: '+18.4 in',
    hBreak: '-6.2 in',
    result: 'Strikeout Swinging',
    thumbnail: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?w=360&auto=format&fit=crop&q=80',
  },
  {
    id: 'clip_2',
    title: 'Sweeper Slider on 1-2 Count (Whiff)',
    velo: '82.6 MPH',
    spin: '2,780 RPM',
    vBreak: '+2.1 in',
    hBreak: '+14.8 in',
    result: 'Swing & Miss',
    thumbnail: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=360&auto=format&fit=crop&q=80',
  },
  {
    id: 'clip_3',
    title: 'Hard Line Drive Double to Left Center (102.4 MPH EV)',
    velo: '102.4 MPH EV',
    spin: '18° Launch',
    vBreak: '388 ft Est.',
    hBreak: 'Barreled',
    result: '2B Double',
    thumbnail: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=360&auto=format&fit=crop&q=80',
  },
]);

const activeClip = ref(clips.value[0]);

const selectClip = (clip: typeof clips.value[0]) => {
  activeClip.value = clip;
  scrubProgress.value = 0;
  isPlaying.value = true;
};

const togglePlay = () => {
  isPlaying.value = !isPlaying.value;
};

const stepFrame = (delta: number) => {
  isPlaying.value = false;
  scrubProgress.value = Math.max(0, Math.min(100, scrubProgress.value + delta));
};

const bookmarkClip = () => {
  notifySuccess(t('videoAnalytics.bookmarked'), t('videoAnalytics.bookmarkedMsg', { title: activeClip.value.title }));
};
</script>

<template>
  <div class="space-y-6 animate-in fade-in duration-200">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('videoAnalytics.title') }}
          </h1>
          <AppBadge variant="accent">4K AI Telestration</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('videoAnalytics.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <div class="w-56">
          <NSelect
            v-model:value="selectedPlayerId"
            :options="playerOptions"
            size="medium"
          />
        </div>
      </div>
    </div>

    <!-- Main Grid: Video Player (8 cols) & Telemetry Metrics (4 cols) -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- 4K Video Player Surface (8 cols) -->
      <div class="lg:col-span-8 bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-2xl space-y-4">
        <!-- Video Screen Container -->
        <div class="relative w-full aspect-video rounded-2xl bg-black overflow-hidden border border-slate-800 group shadow-inner">
          <img
            :src="activeClip.thumbnail"
            :alt="activeClip.title"
            class="w-full h-full object-cover opacity-80"
          />

          <!-- AI Pitch Trajectory Overlay Graphic -->
          <svg class="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 600 340">
            <!-- Strike Zone Box -->
            <rect x="260" y="140" width="80" height="100" fill="rgba(56, 189, 248, 0.08)" stroke="#38bdf8" stroke-width="2" stroke-dasharray="4,4" />
            <line x1="260" y1="173" x2="340" y2="173" stroke="rgba(56, 189, 248, 0.3)" />
            <line x1="260" y1="206" x2="340" y2="206" stroke="rgba(56, 189, 248, 0.3)" />
            <line x1="286" y1="140" x2="286" y2="240" stroke="rgba(56, 189, 248, 0.3)" />
            <line x1="313" y1="140" x2="313" y2="240" stroke="rgba(56, 189, 248, 0.3)" />

            <!-- Pitch Trajectory Arc -->
            <path d="M 120 70 Q 240 120, 325 215" fill="none" stroke="#f59e0b" stroke-width="3.5" />
            <circle cx="325" cy="215" r="7" fill="#ef4444" stroke="#ffffff" stroke-width="2" />
            
            <!-- Pitch Release Point marker -->
            <circle cx="120" cy="70" r="5" fill="#38bdf8" />
            <text x="130" y="75" fill="#38bdf8" font-size="10" font-weight="bold">Release: 5.9 ft</text>
          </svg>

          <!-- Top HUD Badges -->
          <div class="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
            <div class="flex items-center gap-2 bg-slate-950/80 backdrop-blur-md px-3 py-1.5 rounded-xl border border-slate-800 text-xs font-bold text-slate-100">
              <span class="w-2 h-2 rounded-full bg-rose-500 animate-ping mr-1" />
              <span>4K High-Speed 240 FPS &bull; {{ playbackSpeed }}x</span>
            </div>
            <div class="bg-sky-500/20 backdrop-blur-md px-3 py-1.5 rounded-xl border border-sky-500/40 text-xs font-mono font-bold text-sky-300">
              {{ activeClip.velo }}
            </div>
          </div>

          <!-- Center Play Button Overlay when paused -->
          <div
            v-if="!isPlaying"
            @click="togglePlay"
            class="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer"
          >
            <div class="w-16 h-16 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 flex items-center justify-center shadow-2xl transition transform hover:scale-110">
              <Play class="w-7 h-7 ml-1 fill-current" />
            </div>
          </div>

          <!-- Bottom Video Controls Bar -->
          <div class="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black via-slate-950/90 to-transparent flex flex-col gap-2">
            <NSlider
              v-model:value="scrubProgress"
              :step="1"
              :min="0"
              :max="100"
              class="w-full"
            />
            <div class="flex items-center justify-between text-xs text-slate-300">
              <div class="flex items-center gap-2">
                <button @click="togglePlay" class="p-1.5 hover:text-sky-400 transition" title="Play/Pause">
                  <Pause v-if="isPlaying" class="w-4 h-4" />
                  <Play v-else class="w-4 h-4" />
                </button>
                <button @click="stepFrame(-5)" class="p-1.5 hover:text-sky-400 text-slate-400 transition" title="Previous Frame">
                  <SkipBack class="w-3.5 h-3.5" />
                </button>
                <button @click="stepFrame(5)" class="p-1.5 hover:text-sky-400 text-slate-400 transition" title="Next Frame">
                  <SkipForward class="w-3.5 h-3.5" />
                </button>
                <span class="font-mono text-[11px] text-slate-400 ml-1">00:0{{ Math.floor(scrubProgress / 25) }} / 00:04</span>
              </div>

              <!-- Speed & Angle Controls -->
              <div class="flex items-center gap-2">
                <!-- Slow Motion Speed Selectors -->
                <div class="flex items-center bg-slate-900/90 rounded-lg p-0.5 border border-slate-800">
                  <button
                    v-for="s in [0.25, 0.5, 1, 2]"
                    :key="s"
                    @click="playbackSpeed = s"
                    :class="[
                      'px-1.5 py-0.5 rounded text-[10px] font-mono font-bold transition',
                      playbackSpeed === s ? 'bg-sky-500 text-slate-950' : 'text-slate-400 hover:text-white'
                    ]"
                  >
                    {{ s }}x
                  </button>
                </div>

                <!-- Angle Switchers -->
                <div class="flex items-center gap-1">
                  <button
                    v-for="angle in [
                      { id: 'HIGH_HOME', label: 'Home' },
                      { id: 'OPEN_SIDE', label: 'Side' },
                      { id: 'TIGHT_ZONE', label: 'Zone' },
                    ]"
                    :key="angle.id"
                    @click="activeAngle = angle.id as any"
                    :class="[
                      'px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase transition',
                      activeAngle === angle.id ? 'bg-sky-500 text-slate-950' : 'bg-slate-800/80 text-slate-400 hover:text-white'
                    ]"
                  >
                    {{ angle.label }}
                  </button>
                  <button @click="bookmarkClip" class="p-1.5 hover:text-amber-400 transition" title="Bookmark">
                    <Bookmark class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Video Info Summary -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
          <div>
            <h3 class="text-base font-bold text-slate-100">{{ activeClip.title }}</h3>
            <p class="text-xs text-slate-400 mt-0.5">
              Athlete: <strong class="text-slate-200">{{ selectedPlayer.firstName }} {{ selectedPlayer.lastName }}</strong> &bull; Result: <span class="text-emerald-400 font-semibold">{{ activeClip.result }}</span>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <NButton size="small" secondary @click="notifyInfo('Exporting Clip', 'Generating 4K MP4 with telemetry overlay...')">
              <template #icon><Share2 class="w-3.5 h-3.5" /></template>
              Export Video
            </NButton>
          </div>
        </div>
      </div>

      <!-- Telemetry Dashboard & Playlist (4 cols) -->
      <div class="lg:col-span-4 space-y-4">
        <!-- Live AI Telemetry Card -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-4">
          <div class="flex items-center justify-between pb-2 border-b border-slate-800">
            <div class="flex items-center gap-2">
              <Gauge class="w-4 h-4 text-sky-400" />
              <h3 class="text-xs font-bold uppercase tracking-wider text-slate-200">TrackMan Telemetry</h3>
            </div>
            <NTag type="success" size="small" round :bordered="false">Verified</NTag>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-center">
              <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider block">Pitch / Exit Velo</span>
              <span class="text-lg font-mono font-extrabold text-sky-400 mt-0.5 block">{{ activeClip.velo }}</span>
            </div>
            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-center">
              <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider block">Spin Rate</span>
              <span class="text-lg font-mono font-extrabold text-amber-400 mt-0.5 block">{{ activeClip.spin }}</span>
            </div>
            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-center">
              <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider block">Vert. Break (IVB)</span>
              <span class="text-sm font-mono font-extrabold text-emerald-400 mt-0.5 block">{{ activeClip.vBreak }}</span>
            </div>
            <div class="p-3 bg-slate-950 rounded-2xl border border-slate-800 text-center">
              <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider block">Horiz. Break</span>
              <span class="text-sm font-mono font-extrabold text-indigo-400 mt-0.5 block">{{ activeClip.hBreak }}</span>
            </div>
          </div>

          <!-- Coach Feedback Prompt -->
          <div class="p-3.5 bg-slate-950 rounded-2xl border border-slate-800/80 text-xs text-slate-300 space-y-1.5">
            <span class="text-[10px] uppercase tracking-wider font-bold text-slate-400 block">AI Biomechanics Note</span>
            <p class="italic text-slate-300">
              "Optimal hip-to-shoulder separation (34°). Release point consistency within 0.8 inches of season baseline."
            </p>
          </div>
        </div>

        <!-- Clips Reel Playlist -->
        <div class="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
            <Video class="w-4 h-4 text-sky-400" />
            Game Footage Reel
          </h3>

          <div class="space-y-2 max-h-60 overflow-y-auto pr-1">
            <div
              v-for="clip in clips"
              :key="clip.id"
              @click="selectClip(clip)"
              :class="[
                'p-2.5 rounded-2xl border cursor-pointer transition flex items-center gap-3',
                activeClip.id === clip.id ? 'bg-sky-500/15 border-sky-500/60 ring-1 ring-sky-500/30' : 'bg-slate-950 border-slate-800/80 hover:border-slate-700'
              ]"
            >
              <img :src="clip.thumbnail" class="w-12 h-12 rounded-xl object-cover shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-bold text-slate-200 truncate">{{ clip.title }}</p>
                <div class="flex items-center gap-2 mt-1">
                  <span class="text-[10px] font-mono text-sky-400 font-bold">{{ clip.velo }}</span>
                  <span class="text-[10px] text-slate-500">&bull;</span>
                  <span class="text-[10px] text-slate-400">{{ clip.result }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>